import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface GlossaryTerm {
  id: string
  term: string
  abbreviation: string
  domain: string
  definition: string
  owner: string
  status: string
}

interface DataDomain {
  id: string
  name: string
  owner: string
  datasets: number
  terms: number
  quality_score: number
}

interface Statistics {
  business_terms: number
  technical_terms: number
  taxonomies: number
  data_domains: number
  glossary_coverage: number
  term_usage_rate: number
}

export default function MetadataManagementPage() {
  const [glossary, setGlossary] = useState<GlossaryTerm[]>([])
  const [domains, setDomains] = useState<DataDomain[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [glossaryRes, domainsRes, statsRes] = await Promise.all([
        fetch('/api/metadatamanagement/glossary'),
        fetch('/api/metadatamanagement/domains'),
        fetch('/api/metadatamanagement/statistics')
      ])
      if (glossaryRes.ok) setGlossary(await glossaryRes.json())
      if (domainsRes.ok) setDomains(await domainsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching metadata management:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Metadata Management</h1>
          <p className="mt-2 text-gray-600">Phase 270 - Business glossary and taxonomy</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Business Terms</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.business_terms)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Technical Terms</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.technical_terms)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Taxonomies</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.taxonomies}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Data Domains</p>
              <p className="text-2xl font-bold text-green-600">{statistics.data_domains}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Coverage</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.glossary_coverage}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Usage Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.term_usage_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Data Domains</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {domains.map((domain) => (
              <div key={domain.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{domain.name}</span>
                  <span className="text-lg font-bold text-green-600">{domain.quality_score}%</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Datasets</p>
                    <p className="font-medium text-blue-600">{domain.datasets}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Terms</p>
                    <p className="font-medium text-purple-600">{domain.terms}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">Owner: {domain.owner}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Business Glossary</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Term</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abbr</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Definition</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {glossary.map((term) => (
                  <tr key={term.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{term.term}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{term.abbreviation}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{term.domain}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm max-w-xs truncate">{term.definition}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${term.status === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{term.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
